import {
  FormatBoldOutlined,
  FormatItalicOutlined,
  FormatListBulletedOutlined,
  FormatListNumberedOutlined,
  FormatQuoteOutlined,
  FormatUnderlinedOutlined,
  FormatStrikethroughOutlined,
} from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup, Divider, Grid } from '@mui/material';
import type { Editor } from '@tiptap/core';

import { FormatSizeButton } from './FormatSizeButton';
import { UploadImageButton } from './UploadImageButton';
import { EmojiButton } from './EmojiButton';
import { AnchorButton } from './AnchorButton';
import type { RootOwner } from './anchor-button/file-selector/FileSelectorDialog';

interface Props {
  editor: Editor | null;
  rootOwner?: RootOwner;
  disableFileSelector: boolean;
  sticky?: boolean;
}

export const EditorMenu = ({
  editor,
  rootOwner,
  disableFileSelector,
  sticky = false,
}: Props) => {
  if (!editor) {
    return null;
  }

  return (
    <Grid
      container
      flexWrap="wrap"
      spacing={1}
      sx={
        sticky
          ? {
              position: 'sticky',
              bottom: 0,
              py: 1,
              backgroundColor: 'background.paper',
            }
          : { my: 1 }
      }
    >
      <Grid>
        <ToggleButtonGroup
          size="small"
          color="primary"
          aria-label="editor text-format buttons"
        >
          <ToggleButton
            value="bold"
            selected={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldOutlined />
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicOutlined />
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedOutlined />
          </ToggleButton>
          <ToggleButton
            value="strike"
            selected={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <FormatStrikethroughOutlined />
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Divider orientation="vertical" variant="middle" flexItem />
      <Grid>
        <ToggleButtonGroup
          size="small"
          color="primary"
          aria-label="editor list-format buttons"
        >
          <ToggleButton
            value="orderedList"
            selected={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumberedOutlined />
          </ToggleButton>
          <ToggleButton
            value="bulletList"
            selected={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedOutlined />
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Divider orientation="vertical" variant="middle" flexItem />
      <Grid>
        <ToggleButtonGroup
          size="small"
          color="primary"
          aria-label="editor other-format buttons"
        >
          <FormatSizeButton editor={editor} />
          <ToggleButton
            value="blockquote"
            selected={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <FormatQuoteOutlined />
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Divider orientation="vertical" variant="middle" flexItem />
      <Grid>
        <ToggleButtonGroup
          size="small"
          color="primary"
          aria-label="editor buttons"
        >
          <AnchorButton
            editor={editor}
            rootOwner={rootOwner}
            disableFileSelector={disableFileSelector}
          />
          <UploadImageButton editor={editor} />
          <EmojiButton editor={editor} />
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );
};
